import React, { useState, useEffect } from "react";
import { SlidersHorizontal, Save, Store, CreditCard } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { useBranches } from "../../components/finance";

export default function FinanceLiteConfig() {
  const toast = useToast();
  const branches = useBranches();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branchCfg, setBranchCfg] = useState({});
  const [payment, setPayment] = useState({ cash_pct: 0, transfer_pct: 0, qris_pct: 0.7, debit_pct: 0, credit_pct: 1.5 });

  useEffect(() => {
    api.get("/finance-lite/config").then(({ data }) => {
      setCfg(data); setBranchCfg(data.branch_config || {});
      setPayment({ cash_pct: 0, transfer_pct: 0, qris_pct: 0.7, debit_pct: 0, credit_pct: 1.5, ...(data.payment_fees || {}) });
    }).catch(() => toast.error("Gagal memuat konfigurasi")).finally(() => setLoading(false));
  }, [toast]);

  const setBranch = (bid, field, val) => setBranchCfg((p) => ({ ...p, [bid]: { pb1_pct: 10, service_pct: 0, ...(p[bid] || {}), [field]: val === "" ? "" : Number(val) } }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(branchCfg).forEach(([bid, v]) => { clean[bid] = { pb1_pct: Number(v.pb1_pct || 0), service_pct: Number(v.service_pct || 0) }; });
      await api.put("/finance-lite/config", { branch_config: clean, payment_fees: payment });
      toast.success("Konfigurasi disimpan");
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;

  return (
    <div data-testid="finance-lite-config-page">
      <PageHeader title="Konfigurasi Finance Lite" subtitle="Pajak & Service per cabang, biaya marketplace, dan biaya pembayaran (MDR)" icon={SlidersHorizontal}>
        {cfg?.updated_at && <span className="hidden text-xs text-ink-mute sm:inline" data-testid="fl-config-updated">Terakhir diperbarui {new Date(cfg.updated_at).toLocaleString("id-ID")}{cfg.updated_by ? ` · ${cfg.updated_by}` : ""}</span>}
        <Button onClick={save} disabled={saving} data-testid="fl-config-save"><Save size={15} /> {saving ? "Menyimpan…" : "Simpan"}</Button>
      </PageHeader>

      <div className="space-y-5">
        {/* PB1 & Service per branch */}
        <Card className="overflow-hidden p-0" data-testid="fl-tax-config">
          <div className="border-b border-line px-5 py-3"><h3 className="font-heading text-sm font-bold text-ink">Pajak (PB1) & Service Charge per Cabang</h3><p className="text-xs text-ink-mute">Kasir memasukkan Grand Total. Sistem otomatis memecah PB1 & Service dari total (khusus Dine-In / Offline).</p></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                <tr><th className="px-5 py-2">Cabang</th><th className="px-3 py-2 text-right">PB1 (%)</th><th className="px-5 py-2 text-right">Service Charge (%)</th></tr>
              </thead>
              <tbody>
                {branches.map((b) => {
                  const v = branchCfg[b.id] || {};
                  return (
                    <tr key={b.id} className="border-t border-line/60" data-testid={`fl-branch-cfg-${b.id}`}>
                      <td className="px-5 py-2 font-semibold text-ink">{b.name}</td>
                      <td className="px-3 py-2 text-right"><input type="number" step="0.1" className="cf-input w-24 text-right" value={v.pb1_pct ?? 10} onChange={(e) => setBranch(b.id, "pb1_pct", e.target.value)} data-testid={`fl-pb1-${b.id}`} /></td>
                      <td className="px-5 py-2 text-right"><input type="number" step="0.1" className="cf-input w-24 text-right" value={v.service_pct ?? 0} onChange={(e) => setBranch(b.id, "service_pct", e.target.value)} data-testid={`fl-svc-${b.id}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Marketplace (read-only, from sales_channels) */}
        <Card className="p-5" data-testid="fl-marketplace-config">
          <div className="mb-2 flex items-center gap-2"><Store size={16} className="text-primary" /><h3 className="font-heading text-sm font-bold text-ink">Biaya Marketplace</h3></div>
          <p className="mb-3 text-xs text-ink-mute">Sumber tunggal dari modul <b>Sales Channels</b> (komisi per channel). Ubah di sana agar konsisten.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {cfg?.marketplace?.filter((m) => m.is_online).map((m) => (
              <div key={m.key} className="rounded-xl border border-line p-3" data-testid={`fl-mkt-${m.key}`}>
                <p className="text-xs font-bold uppercase text-ink-mute">{m.label}</p>
                <p className="font-heading text-xl font-extrabold text-ink">{m.commission_pct}%</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Payment fees (MDR) — auto-calculated Selling Expense, Offline only */}
        <Card className="p-5" data-testid="fl-payment-config">
          <div className="mb-2 flex items-center gap-2"><CreditCard size={16} className="text-primary" /><h3 className="font-heading text-sm font-bold text-ink">Biaya Pembayaran (MDR)</h3></div>
          <p className="mb-3 text-xs text-ink-mute">Fee gateway per metode pembayaran. Dihitung <b>otomatis</b> dari transaksi <b>Offline</b> di Mini POS dan dicatat sebagai <b>Beban Penjualan</b> (tidak mengurangi Penjualan Bersih). Penjualan online tidak dikenakan.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[["cash_pct", "Tunai (%)"], ["transfer_pct", "Transfer (%)"], ["qris_pct", "QRIS (%)"], ["debit_pct", "Debit (%)"], ["credit_pct", "Kredit (%)"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-semibold text-ink-soft">{label}</label>
                <input type="number" step="0.01" className="cf-input mt-1 w-full" value={payment[k] ?? 0} onChange={(e) => setPayment((p) => ({ ...p, [k]: Number(e.target.value) }))} data-testid={`fl-pay-${k}`} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
